import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

// VAPID public key — this is intentionally hardcoded (public keys are not secret)
const VAPID_PUBLIC_KEY = "BER9q-qS7CAWoHmPOOj1D8wOxA8cebv8mtLpsFnQaEvAdNUU5O3s25Lhu_YH1z2TozZv7qs3qRkbqMdOMyFzUO4";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

// Set when the user explicitly turns notifications off on this device, so the
// auto-healer below never overrides a deliberate choice. Survives cache clears
// (localStorage is not touched by Clear Cache & Reload).
const PUSH_OPT_OUT_KEY = "push-opted-out";

/**
 * Silently restores this device's push subscription when it was lost through
 * no choice of the user's — historically Clear Cache & Reload unregistered the
 * service worker, and the push subscription died with it, so users stopped
 * getting notifications without knowing anything changed.
 *
 * Never prompts: it only acts when browser permission is ALREADY granted and
 * the user hasn't explicitly toggled notifications off on this device.
 * Mounted once in the app shell; runs after sign-in resolves.
 */
export function usePushSubscriptionHealer() {
  useEffect(() => {
    let cancelled = false;

    const heal = async (userId: string) => {
      try {
        if (
          !("serviceWorker" in navigator) ||
          !("PushManager" in window) ||
          !("Notification" in window)
        ) return;
        if (Notification.permission !== "granted") return;
        if (localStorage.getItem(PUSH_OPT_OUT_KEY) === "true") return;

        const reg = await navigator.serviceWorker.ready;
        const existing = await reg.pushManager.getSubscription();
        if (existing || cancelled) return;

        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource,
        });
        const json = sub.toJSON();
        const keys = json.keys as { p256dh: string; auth: string };
        await supabase.from("push_subscriptions").upsert(
          {
            user_id: userId,
            endpoint: json.endpoint!,
            p256dh: keys.p256dh,
            auth: keys.auth,
            user_agent: navigator.userAgent,
          },
          { onConflict: "user_id,endpoint" }
        );
      } catch {
        // Best effort — a failed heal is just the status quo.
      }
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) heal(session.user.id);
    });

    return () => {
      cancelled = true;
    };
  }, []);
}

export function usePushNotifications(
  userId: string | undefined,
  roles: string[]
) {
  const [permission, setPermission] = useState<NotificationPermission>("default");
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Only supported if browser has the APIs and a VAPID key is configured
  const isSupported =
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window &&
    !!VAPID_PUBLIC_KEY;

  // All authenticated users are eligible for push notifications
  const isEligible = true;

  useEffect(() => {
    if (!isSupported) return;
    setPermission(Notification.permission);
    if (!userId) return;

    // Reconcile browser subscription against DB. If the browser still has a
    // PushSubscription but the DB row is gone (e.g. admin revoked), tear down
    // the browser subscription too so the Settings toggle reflects reality.
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!sub) {
          setIsSubscribed(false);
          return;
        }
        const { data: row, error } = await supabase
          .from("push_subscriptions")
          .select("endpoint")
          .eq("user_id", userId)
          .eq("endpoint", sub.endpoint)
          .maybeSingle();
        if (error) {
          // If the DB lookup fails, fall back to browser state — don't
          // accidentally unsubscribe on a transient error.
          setIsSubscribed(true);
          return;
        }
        if (!row) {
          try {
            await sub.unsubscribe();
          } catch {
            // ignore — worst case the browser keeps a dead subscription
          }
          setIsSubscribed(false);
        } else {
          setIsSubscribed(true);
        }
      } catch {
        // Service worker unavailable — leave subscribed state false.
        setIsSubscribed(false);
      }
    })();
  }, [isSupported, userId]);

  const subscribe = useCallback(async () => {
    if (!isSupported || !isEligible || !userId) return;
    setIsLoading(true);
    try {
      const permission = await Notification.requestPermission();
      setPermission(permission);
      if (permission !== "granted") return;

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY!) as BufferSource,
      });

      const json = sub.toJSON();
      const keys = json.keys as { p256dh: string; auth: string };

      await supabase.from("push_subscriptions").upsert(
        {
          user_id: userId,
          endpoint: json.endpoint!,
          p256dh: keys.p256dh,
          auth: keys.auth,
          user_agent: navigator.userAgent,
        },
        { onConflict: "user_id,endpoint" }
      );

      localStorage.removeItem(PUSH_OPT_OUT_KEY);
      setIsSubscribed(true);
    } catch (err) {
      console.error("[push] subscribe error:", err);
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, isEligible, userId]);

  const unsubscribe = useCallback(async () => {
    if (!userId) return;
    setIsLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await sub.unsubscribe();
        await supabase
          .from("push_subscriptions")
          .delete()
          .eq("user_id", userId)
          .eq("endpoint", sub.endpoint);
      }
      // Deliberate opt-out: remember it so the auto-healer doesn't resubscribe.
      localStorage.setItem(PUSH_OPT_OUT_KEY, "true");
      setIsSubscribed(false);
    } catch (err) {
      console.error("[push] unsubscribe error:", err);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  return { isSupported, isEligible, permission, isSubscribed, isLoading, subscribe, unsubscribe };
}
